import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FileStack, Loader2, Users } from 'lucide-react';

import {
    fetchAddableUsers,
    fetchRemovableUsers,
    fetchBudgetViewAccessList,
    saveBudgetViewAttachmentAccess,
    clearBudgetViewAttachmentSaveResult,
    selectAddableUsers,
    selectRemovableUsers,
    selectAccessList,
    selectAccessLoading,
    selectAccessError,
    selectSaveLoading,
} from '../../slices/accountsSlice/budgetViewAttachmentConfigSlice';
import { showToast } from '../../utilities/toastUtilities';

const BudgetViewAttachmentConfig = () => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userDetails?.userName || 'system';

    const addableUsers   = useSelector(selectAddableUsers);
    const removableUsers = useSelector(selectRemovableUsers);
    const accessList     = useSelector(selectAccessList);
    const accessLoading  = useSelector(selectAccessLoading);
    const accessError    = useSelector(selectAccessError);
    const saveLoading    = useSelector(selectSaveLoading);

    const [actionType, setActionType] = useState('Add');
    const [selectedUserId, setSelectedUserId] = useState('');

    const load = useCallback(() => {
        dispatch(fetchAddableUsers());
        dispatch(fetchRemovableUsers());
        dispatch(fetchBudgetViewAccessList());
    }, [dispatch]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => { setSelectedUserId(''); }, [actionType]);

    const userOptions = actionType === 'Add' ? addableUsers : removableUsers;

    const handleReset = () => {
        setActionType('Add');
        setSelectedUserId('');
    };

    const handleSubmit = async () => {
        const user = userOptions.find(u => u.userId === selectedUserId);
        if (!user) {
            showToast('error', 'Please select a user.');
            return;
        }
        try {
            await dispatch(saveBudgetViewAttachmentAccess({
                actionType,
                userId:    user.userId,
                userName:  user.userName,
                createdBy: currentUser,
            })).unwrap();
            showToast('success', actionType === 'Add'
                ? `${user.userName} can now view budget attachment reports.`
                : `${user.userName}'s access was removed.`);
            handleReset();
            load();
        } catch (err) {
            showToast('error', typeof err === 'string' ? err : 'Failed to update access.');
        } finally {
            dispatch(clearBudgetViewAttachmentSaveResult());
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* Page Header */}
            <div className="max-w-4xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-6 text-white">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20 flex-shrink-0">
                            <FileStack className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">
                                Configuration
                            </span>
                            <h1 className="text-xl md:text-2xl font-black tracking-tight mt-1">Budget View Attachment Configuration</h1>
                            <p className="text-indigo-200 text-sm mt-0.5">
                                Control which employees can view budget attachment reports.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto space-y-4">

                {/* Add / Remove form */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">Add / Remove</label>
                            <select
                                value={actionType}
                                onChange={(e) => setActionType(e.target.value)}
                                className="w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all"
                            >
                                <option value="Add">Add</option>
                                <option value="Remove">Remove</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">User Name</label>
                            <select
                                value={selectedUserId}
                                onChange={(e) => setSelectedUserId(e.target.value)}
                                className="w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all"
                            >
                                <option value="">-Please Select-</option>
                                {userOptions.map(u => (
                                    <option key={u.userId} value={u.userId}>{u.userName}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            type="button"
                            onClick={handleReset}
                            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                            Reset
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={!selectedUserId || saveLoading}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 shadow-md disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                        >
                            {saveLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                            Submit
                        </button>
                    </div>
                </div>

                {/* Current access list */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {accessLoading ? (
                        <div className="py-16 flex flex-col items-center justify-center gap-3">
                            <Loader2 className="h-6 w-6 text-indigo-500 animate-spin" />
                            <span className="text-sm text-gray-400">Loading current access…</span>
                        </div>
                    ) : accessError ? (
                        <div className="py-10 text-center text-sm text-rose-500">{accessError}</div>
                    ) : accessList.length === 0 ? (
                        <div className="py-16 text-center text-gray-400 dark:text-gray-500 text-sm">
                            <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
                            No employees currently have access.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">User ID</th>
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                                        <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">User Role</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                    {accessList.map((u) => (
                                        <tr key={u.userId} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                            <td className="px-4 py-3 font-mono text-gray-500 dark:text-gray-400">{u.userId}</td>
                                            <td className="px-4 py-3 font-semibold text-gray-800 dark:text-gray-100">{u.userName}</td>
                                            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{u.userRole || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BudgetViewAttachmentConfig;
